/**
* Hreflang cluster helpers for the Store cluster (pillar + product +
* category + bundle). Only shipped locales are advertised; TH is the
* x-default because product detail pages are TH-only in this wave.
*/
const BASE = "https://thainotarylaw.com";
const SHIPPED = ["th", "en", "zh", "ja", "ko", "vi", "id"] as const;

/** Cluster for /store pillar (all 7 locales shipped). */
export function storePillarHreflang() {
  return [
    ...SHIPPED.map((l) => ({
      rel: "alternate" as const,
      hrefLang: l === "th" ? "th-TH" : l,
      href: l === "th" ? `${BASE}/store` : `${BASE}/${l}/store`,
    })),
    { rel: "alternate" as const, hrefLang: "x-default", href: `${BASE}/store` },
  ];
}

/**
* Cluster for a specific store leaf where localized mirrors do NOT exist
* yet — all locales point to the Thai canonical (self-referential Thai +
* x-default). This keeps ranking signals consolidated on one URL until
* product detail pages are localized in a later wave.
*/
export function storeLeafHreflangTHOnly(path: string) {
  const abs = `${BASE}${path}`;
  return [
    { rel: "alternate" as const, hrefLang: "th-TH", href: abs },
    { rel: "alternate" as const, hrefLang: "x-default", href: abs },
  ];
}

const HREFLANG_MAP: Record<(typeof SHIPPED)[number], string> = {
  th: "th-TH", en: "en", zh: "zh-CN", ja: "ja-JP", ko: "ko-KR", vi: "vi-VN", id: "id-ID",
};

/** Cluster for a product detail page — Thai canonical + 6 locale mirrors. */
export function storeProductHreflang(slug: string) {
  return [
    ...SHIPPED.map((l) => ({
      rel: "alternate" as const,
      hrefLang: HREFLANG_MAP[l],
      href: l === "th" ? `${BASE}/store/${slug}` : `${BASE}/${l}/store/${slug}`,
    })),
    { rel: "alternate" as const, hrefLang: "x-default", href: `${BASE}/store/${slug}` },
  ];
}

/** Cluster for a category leaf — Thai canonical + 6 locale mirrors. */
export function storeCategoryHreflang(categoryId: string) {
  return [
    ...SHIPPED.map((l) => ({
      rel: "alternate" as const,
      hrefLang: HREFLANG_MAP[l],
      href: l === "th"
        ? `${BASE}/store/category/${categoryId}`
        : `${BASE}/${l}/store/category/${categoryId}`,
    })),
    { rel: "alternate" as const, hrefLang: "x-default", href: `${BASE}/store/category/${categoryId}` },
  ];
}

/** Cluster for a bundle leaf — Thai canonical + 6 locale mirrors. */
export function storeBundleHreflang(slug: string) {
  return [
    ...SHIPPED.map((l) => ({
      rel: "alternate" as const,
      hrefLang: HREFLANG_MAP[l],
      href: l === "th"
        ? `${BASE}/store/bundles/${slug}`
        : `${BASE}/${l}/store/bundles/${slug}`,
    })),
    { rel: "alternate" as const, hrefLang: "x-default", href: `${BASE}/store/bundles/${slug}` },
  ];
}
